import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FieldTree } from "@angular/forms/signals";
import { DspAdvertiserDeal, DspAdvertiserDealDealTypeEnum, StrategyDspCreativeType } from "@front/m19-api-client";
import { findDealWithoutAudienceTargeting } from "@front/m19-services";
import { IDateRange } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { DateTime } from "luxon";
import { DspFormService } from "@m19-board/dsp/dsp-create-strategy/dsp-form.service";
import { DspCreateStrategyAudiencesModel } from "@m19-board/dsp/dsp-create-strategy/steps/dsp-create-strategy-audiences-step.component";
import { DspCreateStrategyBudgetModel } from "@m19-board/dsp/dsp-create-strategy/steps/dsp-create-strategy-budget-step.component";
import { DspMediaSelectorComponent } from "@m19-board/dsp/dsp-media/dsp-media-selector.component";
import { DspDealSelectorComponent } from "@m19-board/dsp/dsp-media/dsp-deal-selector.component";

export interface DspCreateStrategyMediaModel {
  creativeType: StrategyDspCreativeType | null;
  dealId: string | null;
}

type AwarenessMedia = StrategyDspCreativeType.STREAMING_TV | StrategyDspCreativeType.VIDEO_DEAL;

@Component({
  selector: "app-dsp-create-strategy-media-step",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DspMediaSelectorComponent, DspDealSelectorComponent, TranslocoDirective],
  template: `
    <div class="flex flex-col gap-6 py-8" *transloco="let t">
      <app-dsp-media-selector
        [media]="selectedMedia()"
        [stvAvailable]="stvAvailable()"
        [videoDealAvailable]="videoDealAvailable()"
        (mediaChange)="selectMedia($event)"
      />
      @if (isVideoDeal()) {
        <div class="flex flex-col gap-2">
          <app-dsp-deal-selector
            [deals]="videoDeals()"
            [dealId]="media().dealId().value() ?? undefined"
            (dealIdChange)="selectDealId($event)"
          >
            <div class="flex flex-col gap-2">
              <div class="type-body-medium text-foreground">{{ t("dsp-media-selector.select_deal") }}</div>
              <p class="type-label text-muted-foreground m-0">{{ t("dsp-media-selector.video_deal_deals_intro") }}</p>
            </div>
          </app-dsp-deal-selector>
        </div>
      }
    </div>
  `,
})
export class DspCreateStrategyMediaStepComponent {
  private readonly dspFormService = inject(DspFormService);

  readonly media = input.required<FieldTree<DspCreateStrategyMediaModel>>();
  readonly budget = input.required<FieldTree<DspCreateStrategyBudgetModel>>();
  readonly audiences = input.required<FieldTree<DspCreateStrategyAudiencesModel>>();

  readonly stvAvailable = toSignal(this.dspFormService.stvAvailable$, { initialValue: true });
  readonly videoDealAvailable = toSignal(this.dspFormService.videoDealAvailable$, { initialValue: true });
  readonly videoDeals = toSignal(this.dspFormService.videoDeals$, { initialValue: [] });

  readonly selectedMedia = computed((): AwarenessMedia | undefined => {
    const creativeType = this.media().creativeType().value();
    return creativeType === StrategyDspCreativeType.STREAMING_TV || creativeType === StrategyDspCreativeType.VIDEO_DEAL
      ? creativeType
      : undefined;
  });

  readonly isVideoDeal = computed(() => this.selectedMedia() === StrategyDspCreativeType.VIDEO_DEAL);

  private readonly selectedGuaranteedDeal = computed(() => this.guaranteedDeal(this.media().dealId().value()));

  selectMedia(creativeType: StrategyDspCreativeType | undefined) {
    if (!creativeType) return;
    this.media().creativeType().markAsDirty();
    this.media().creativeType().value.set(creativeType);

    if (creativeType !== StrategyDspCreativeType.VIDEO_DEAL) {
      this.selectDealId(undefined);
    }
  }

  selectDealId(dealId: string | undefined) {
    const previousGuaranteedDeal = this.selectedGuaranteedDeal();

    this.media().dealId().markAsDirty();
    this.media()
      .dealId()
      .value.set(dealId ?? null);

    this.applyGuaranteedDealFlight(previousGuaranteedDeal);
    if (findDealWithoutAudienceTargeting(this.videoDeals(), dealId ? [dealId] : [])) {
      this.audiences().audiences().value.set([[]]);
      this.audiences().blacklistedAudiences().value.set([[]]);
    }
  }

  private applyGuaranteedDealFlight(previousGuaranteedDeal: DspAdvertiserDeal | undefined) {
    const deal = this.selectedGuaranteedDeal();

    if (deal) {
      this.setDealDrivenValues(deal);
    } else if (previousGuaranteedDeal) {
      this.budget().budget().reset(null);
      this.budget().budgetDates().reset(null);
    }
  }

  private setDealDrivenValues(deal: DspAdvertiserDeal) {
    this.budget()
      .budget()
      .value.set(deal.budget ?? null);

    this.budget().budget().markAsDirty();
    this.budget()
      .budgetDates()
      .value.set(dealFlightDates(deal) ?? null);
    this.budget().budgetDates().markAsDirty();
  }

  private guaranteedDeal(dealId: string | null): DspAdvertiserDeal | undefined {
    const deal = dealId ? this.videoDeals().find((d) => d.dealId === dealId) : undefined;
    return deal?.dealType === DspAdvertiserDealDealTypeEnum.PROGRAMMATIC_GUARANTEED ? deal : undefined;
  }
}

function dealFlightDates(deal: DspAdvertiserDeal | undefined): IDateRange | undefined {
  if (!deal?.startDate || !deal.endDate) return undefined;
  return {
    start: DateTime.fromISO(deal.startDate, { zone: "utc" }),
    end: DateTime.fromISO(deal.endDate, { zone: "utc" }),
  };
}
